import React, { useState, useEffect } from "react";
import { Grid2, Button, Typography, CircularProgress, Chip } from "@mui/material";
import { PlayArrow, CheckCircle, Error, Schedule, Download, SwapHoriz } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card.js";
import { triggerGeneticEvaluation, triggerRelationshipsCalculation, getActionsStatuses, downloadActionResults } from "../api/index.js";
import { useSnackbar } from "../utils/index.js";
import Spinner from "../components/Spinner.js";

const AdminActions = () => {
  const [actions, setActions] = useState({});
  const [loading, setLoading] = useState(false);

  const { error, success } = useSnackbar();
  const navigate = useNavigate();

  // Available admin actions
  const adminActions = [
    {
      id: "genetic-evaluation",
      title: "Γενετική Αξιολόγηση",
      description: "Εκτέλεση διαδικασίας γενετικής αξιολόγησης για όλα τα ζώα",
      color: "primary"
    },
    {
      id: "relationships-calculation",
      title: "Υπολογισμός Συγγενειών",
      description: "Υπολογισμός του βαθμού συγγένειας για όλα τα ζώα",
      color: "primary"
    },
  ];

  useEffect(() => {
    fetchActionStatuses();
  }, []);

  const fetchActionStatuses = async () => {
    try {
      const response = await getActionsStatuses();

      // Map the response to actions state
      const mappedActions = {};

      if (response?.geneticEvaluationLastExecution) {
        mappedActions["genetic-evaluation"] = {
          id: "genetic-evaluation",
          status: response.geneticEvaluationLastExecution.status,
          lastRun: response.geneticEvaluationLastExecution.startedAt,
          error: response.geneticEvaluationLastExecution.error,
          duration: response.geneticEvaluationLastExecution.duration
        };
      }

      if (response?.relationshipsLastExecution) {
        mappedActions["relationships-calculation"] = {
          id: "relationships-calculation",
          status: response.relationshipsLastExecution.status,
          lastRun: response.relationshipsLastExecution.startedAt,
          error: response.relationshipsLastExecution.error,
          duration: response.relationshipsLastExecution.duration
        };
      }

      setActions(mappedActions);
    } catch (error) {
      console.error("Error fetching action statuses:", error);
    }
  };

  const executeAction = async (actionId) => {
    setLoading(true);
    try {
      let response;

      if (actionId === "genetic-evaluation") {
        response = await triggerGeneticEvaluation();
      } else if (actionId === "relationships-calculation") {
        response = await triggerRelationshipsCalculation();
      }

      if (!response.success) {
        error(response.message);
      } else {
        success(response.message);
        fetchActionStatuses();
      }
    } catch (err) {
      console.error(`Error executing ${actionId}:`, err);
      error("Σφάλμα κατά την εκτέλεση της ενέργειας");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle sx={{ color: "success.main" }} />;
      case "processing":
        return <CircularProgress size={20} />;
      case "failed":
        return <Error sx={{ color: "error.main" }} />;
      default:
        return <Schedule sx={{ color: "text.secondary" }} />;
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { label: "Ολοκληρωμένη", color: "success" },
      processing: { label: "Σε Εξέλιξη", color: "secondary" },
      failed: { label: "Αποτυχία", color: "error" },
      idle: { label: "Αδρανής", color: "info" }
    };

    const config = statusConfig[status] || statusConfig.idle;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={getStatusIcon(status)}
      />
    );
  };

  const formatLastRun = (timestamp) => {
    if (!timestamp) return "Ποτέ";

    const date = new Date(timestamp);
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    };

    return date.toLocaleDateString("el-GR", options);
  };

  const handleDownloadFiles = async (actionId) => {
    setLoading(true);
    try {
      // Get the blob using the API function
      const blob = await downloadActionResults(actionId);

      // Check if we got a valid blob
      if (!blob || blob.size === 0) {
        throw new Error("Invalid file response");
      }

      // Create a download URL from the blob
      const url = window.URL.createObjectURL(blob);

      // Create and configure the download link
      const a = document.createElement("a");
      a.href = url;
      a.download = `${actionId}-results.zip`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      // Check if it"s a specific error from the server
      if (err.response) {
        try {
          const errorText = await err.response.text();
          const errorData = JSON.parse(errorText);

          if (errorData.error && errorData.error.includes("File not found")) {
            error("Τα αρχεία αποτελεσμάτων δεν υπάρχουν ακόμα. Παρακαλώ εκτελέστε πρώτα τη διαδικασία και περιμένετε να ολοκληρωθεί.");
            return;
          }
        } catch {
          // Ignore JSON parsing errors
        }
      }

      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();

        if (errorMessage.includes("404")) {
          error("Τα αρχεία αποτελεσμάτων δεν βρέθηκαν. Παρακαλώ εκτελέστε πρώτα τη διαδικασία και περιμένετε να ολοκληρωθεί.");
        } else if (errorMessage.includes("network") || errorMessage.includes("failed to fetch")) {
          error("Πρόβλημα σύνδεσης με τον διακομιστή");
        } else {
          error("Σφάλμα κατά τη λήψη των αρχείων");
        }
      } else {
        error("Άγνωστο σφάλμα κατά τη λήψη των αρχείων");
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Spinner open={loading} />
      <Grid2 container spacing={3} padding={2}>
        <Grid2 size={12}>
          <Typography variant="h4" component="h1" gutterBottom>
            Ενέργειες Διαχειριστή
          </Typography>
        </Grid2>

        {/* Owner Display Code Proxy Mapping Card */}
        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
          <Card
            title={
              <Grid2 container alignItems="center" justifyContent="space-between" width="100%">
                <Typography variant="h6">Αντιστοίχιση Κωδικών Εκτροφής</Typography>
              </Grid2>
            }
            titleBackgroundColor="primary"
            height="300px"
            width="100%"
          >
            <Grid2 container direction="column" spacing={2} height="100%">
              <Grid2>
                <Typography variant="body2" color="text.secondary">
                  Διαχείριση κωδικών εμφάνισης για εκτροφές. Ορίστε ποιον κωδικό βλέπει ο χρήστης αντί του πραγματικού κωδικού της εκτροφής.
                </Typography>
              </Grid2>
              <Grid2 sx={{ mt: "auto" }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<SwapHoriz />}
                  onClick={() => navigate("/owners/display-codes")}
                  sx={{ mt: 2 }}
                >
                  Διαχείριση Αντιστοιχίσεων
                </Button>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>

        {adminActions.map((adminAction) => {
          const actionData = actions[adminAction.id] || {};
          const isProcessing = actionData.status === "processing";

          return (
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }} key={adminAction.id}>
              <Card
                title={
                  <Grid2 container alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="h6">{adminAction.title}</Typography>
                    {getStatusChip(actionData.status || "idle")}
                  </Grid2>
                }
                titleBackgroundColor={adminAction.color}
                height="300px"
                width="100%"
              >
                <Grid2 container direction="column" spacing={2} height="100%">
                  <Grid2>
                    <Typography variant="body2" color="text.secondary">
                      {adminAction.description}
                    </Typography>
                  </Grid2>

                  <Grid2 sx={{ mt: "auto" }}>
                    <Typography variant="body2" gutterBottom>
                      Τελευταία Εκτέλεση: <strong>{formatLastRun(actionData.lastRun)}</strong>
                    </Typography>

                    {actionData.duration && (
                      <Typography variant="body2" gutterBottom>
                        Διάρκεια: <strong>{actionData.duration ? `${actionData.duration.toFixed(2)} δευτερόλεπτα` : "Δ/Υ"}</strong>
                      </Typography>
                    )}

                    {actionData.error && (
                      <Typography variant="body2" color="error" gutterBottom>
                        Σφάλμα: {actionData.error}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      color={adminAction.color}
                      fullWidth
                      startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                      onClick={() => executeAction(adminAction?.id)}
                      disabled={loading || isProcessing}
                      sx={{ mt: 2 }}
                    >
                      {loading ? "Εκκίνηση..." :
                        isProcessing ? "Σε Εξέλιξη..." : "Εκτέλεση"}
                    </Button>

                    {actionData.status === "completed" && (
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<Download />}
                        onClick={() => handleDownloadFiles(adminAction.id)}
                        sx={{ mt: 1 }}
                      >
                        Λήψη Αποτελεσμάτων
                      </Button>
                    )}
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>
    </>
  );
};

export default AdminActions;
